import { ReactNode, useEffect, useState } from 'react'
import theme from 'theme'

/* Utils */
import { accountSettingsSelector, setAccountSettings } from '../../../../store/user/userSlice'
import { Tenant, UserRoleSummary } from '../../../../models'
import axios from '../../../../lib/axios'
import { UserServiceV4 } from 'services'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { closeToast, openToast, ToastVariant } from '../../../../store/toasts/toastSlice'

/* Components */
import AccountInformation from './components/AccountInformation'
import SSOsupport from './components/SSOsupport'
import NexaSettings from './components/NexaSettings'
import EditCompanyInfoDrawer from './components/EditCompanyInfoDrawer'
import { Box, CircularProgress, Typography } from '@mui/material'
import {
  Container,
  ContentWrapperMain,
  PageHeader,
  PageTitle,
  TabsContainer,
  TabItem
} from './account-management.styles'
import { useAuth } from '@frontegg/react'
import { DEMO_ROLE_NAME, hasAnyRole, Role } from 'lib/utils'

interface IIndustries {
  label: string
  value: string
}

interface TabPanelProps {
  children?: ReactNode
  index: number
  value: number
}

const AccountManagement = () => {
  const dispatch = useAppDispatch()
  const { user: authUser } = useAuth()
  const account = useAppSelector(accountSettingsSelector)
  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(true)
  const [roles, setRoles] = useState<UserRoleSummary[]>([])
  const [industries, setIndustries] = useState<IIndustries[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form, setForm] = useState({} as Tenant)

  useEffect(() => {
    fetchAccountData().catch((e) => e)
    fetchIndustries().catch((e) => e)
    fetchRoles().catch((e) => e)
  }, [])

  const fetchRoles = async () => {
    try {
      const options = ['Admin', 'CPI Manager', 'Viewer', DEMO_ROLE_NAME]
      const list = (await UserServiceV4.getRoles()).filter((item) => options.includes(item.name))
      const manager = list.find((item) => item.key === 'cpi_manager')
      if (manager) manager.name = 'Manager'
      setRoles(list)
    } catch (err) {
      console.error(err)
    }
  }

  const fetchAccountData = async () => {
    try {
      const { data, status } = await axios.get('/api/v3/tenant')
      if (status === 200) {
        dispatch(setAccountSettings(data.data))
        setForm(data.data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchIndustries = async () => {
    try {
      const { data, status } = await axios.get('/api/v3/tenant/industries')
      if (status === 200) {
        const arr = [] as IIndustries[]
        data.data.forEach((item: string) => {
          arr.push({
            label: item,
            value: item
          })
        })
        setIndustries(arr)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const onSubmit = async (values: Tenant) => {
    setLoading(true)
    try {
      const data = {
        logo: values.logo,
        name: values.name,
        timeZone: values.timeZone,
        industry: values.industry,
        cyFramework: values.cyFramework
      }

      await axios.put('/api/v1/tenant', data)
      localStorage.setItem('framework', JSON.stringify(values.cyFramework))
      setDrawerOpen(false)
      await fetchAccountData()
    } catch (err) {
      console.error(err)
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to update company information.',
            description: 'Your changes could not be saved. Please try again.'
          }
        })
      )
      setTimeout(() => {
        dispatch(closeToast())
      }, 6000)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDrawer = () => {
    setForm(account as Tenant)
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setForm(account as Tenant)
    setDrawerOpen(false)
  }

  const canEdit = hasAnyRole([Role.Owner, Role.Admin], authUser)

  return (
    <Container>
      <PageHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>Account Settings</PageTitle>
        </Box>

        <TabsContainer>
          <TabItem isActive={tabValue === 0} onClick={() => setTabValue(0)}>
            <Typography className="tab-label">Company Information</Typography>
          </TabItem>
          <TabItem isActive={tabValue === 1} onClick={() => setTabValue(1)}>
            <Typography className="tab-label">SSO</Typography>
          </TabItem>
          <TabItem isActive={tabValue === 2} onClick={() => setTabValue(2)}>
            <Typography className="tab-label">Nexa AI</Typography>
          </TabItem>
        </TabsContainer>
      </PageHeader>

      <ContentWrapperMain sx={{ background: theme.colors.pageBackground }}>
        {loading ? (
          <Box className="loading-wrapper">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              <AccountInformation onEditClick={handleOpenDrawer} canEdit={canEdit} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <SSOsupport roles={roles} />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <NexaSettings />
            </TabPanel>
          </>
        )}
      </ContentWrapperMain>

      <EditCompanyInfoDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        form={form}
        industries={industries}
        onSubmit={onSubmit}
      />
    </Container>
  )
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`crowdstrike-tabpanel-${index}`}
      aria-labelledby={`crowdstrike-tab-${index}`}
      {...other}
      style={{
        padding: '40px 72px 60px'
      }}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

export default AccountManagement
